import React, { useState, useEffect } from 'react';
import { Shield, Mail, Lock, Key, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const AccountSettings: React.FC = () => {
  const { 
    user, 
    updateUserDisplayName, 
    updateEmail, 
    updatePassword,
    autoLockTime = 300000, // Set default value
    updateAutoLockTime,
    deleteAccount,
    signOut
  } = useAuth();
  const { toast } = useToast();
  
  const [newDisplayName, setNewDisplayName] = useState(user?.displayName || '');
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  // Masterkey management removed
  const [selectedAutoLockTime, setSelectedAutoLockTime] = useState(autoLockTime || 300000);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  // Masterkey management removed
  
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast({
        title: "Informações em falta",
        description: "Preencha todos os campos de palavra-passe",
        variant: "destructive"
      });
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast({
        title: "As palavras-passe não correspondem",
        description: "As novas palavras-passe que introduziu não correspondem. Por favor, tente novamente.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setPasswordLoading(true);
      console.log("updatePassword is:", typeof updatePassword);
      
      if (typeof updatePassword !== 'function') {
        throw new Error("A função de atualização da palavra-passe não está disponível");
      }
      
      await updatePassword(currentPassword, newPassword);
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      
      toast({
        title: "Palavra-passe atualizada",
        description: "A sua palavra-passe foi atualizada com sucesso.d",
      });
    } catch (error: any) {
      console.error("Password update error:", error);
      
      let errorMessage = "Falha ao atualizar a palavra-passe. Por favor, tente novamente.";
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = "Palavra-passe atual incorreta. Por favor, tente novamente.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "A nova palavra-passe é demasiado fraca. Utilize uma palavra-passe mais forte.";
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = "Por motivos de segurança, faça logout e login novamente antes de alterar a sua palavra-passe..";
        // Sign the user out to force re-authentication
        try {
          await signOut();
        } catch (signOutError) {
          console.error("Falha ao sair:", signOutError);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Falha na atualização",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Masterkey update flow removed
  
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast({
        title: "Erro",
        description: "Por favor, introduza a sua palavra-passe",
        variant: "destructive",
      });
      return;
    }

    try {
      setDeleteLoading(true);
      console.log("deleteAccount is:", typeof deleteAccount, deleteAccount);
      
      if (typeof deleteAccount === 'function') {
        await deleteAccount(deletePassword);
        setShowDeleteConfirm(false);
      } else {
        throw new Error("A função de eliminar conta não está disponível");
      }
    } catch (error: any) {
      console.error("Delete account error:", error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao eliminar a conta. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handleUpdateAutoLockTime = async (time: string) => {
    try {
      const timeInMs = parseInt(time);
      if (!isNaN(timeInMs)) {
        await updateAutoLockTime(timeInMs);
        setSelectedAutoLockTime(timeInMs);
        
        toast({
          title: 'Bloqueio automático atualizado',
          description: 'O tempo de bloqueio automático foi atualizado',
          duration: 2000,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Tabs defaultValue="account" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="account">Conta</TabsTrigger>
          <TabsTrigger value="security">Segurança</TabsTrigger>
          <TabsTrigger value="vault">Definições do cofre</TabsTrigger>
        </TabsList>
        
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Informações da conta</CardTitle>
              <CardDescription>
                Ver os detalhes da sua conta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-1">
                <Label>Endereço de e-mail</Label>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-muted-foreground" />
                  <span>{user?.email}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Os endereços de e-mail não podem ser alterados após a criação da conta.
                </p>
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-medium text-destructive">Zona de Perigo</h3>
                  <p className="text-sm text-muted-foreground">
                    Depois de eliminar a sua conta, não há como voltar atrás. Tenha a certeza do que está a fazer.
                  </p>
                </div>
                
                <Button 
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 size={16} className="mr-2" />
                  Eliminar conta
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Definições de segurança</CardTitle>
              <CardDescription>
                Aumente a segurança da sua conta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <h3 className="text-lg font-medium">Alterar palavra-passe</h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="currentPassword">Palavra-passe atual</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="newPassword">Nova palavra-passe</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="confirmNewPassword">Confirmar nova palavra-passe</Label>
                    <Input
                      id="confirmNewPassword"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />
                  </div>
                </div>
                <Button type="submit" disabled={passwordLoading}>
                  {passwordLoading ? 'Atualizando...' : 'Atualizar palavra-passe'}
                </Button>
              </form>
              {/* Masterkey settings removed */}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="vault">
          <Card>
            <CardHeader>
              <CardTitle>Definições de bloqueio automático</CardTitle>
              <CardDescription>
                Configure quando o seu cofre é bloqueado automaticamente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Temporizador de bloqueio automático</Label>
                  <RadioGroup
                    value={selectedAutoLockTime.toString()}
                    onValueChange={handleUpdateAutoLockTime}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="never" />
                      <Label htmlFor="never">Nunca</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="300000" id="5min" />
                      <Label htmlFor="5min">5 minutos</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="900000" id="15min" />
                      <Label htmlFor="15min">15 minutos</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1800000" id="30min" />
                      <Label htmlFor="30min">30 minutos</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="3600000" id="1hour" />
                      <Label htmlFor="1hour">1 hora</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="7200000" id="2hours" />
                      <Label htmlFor="2hours">2 horas</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertTitle>Nota de segurança</AlertTitle>
                  <AlertDescription>
                     Por motivos de segurança, recomendamos definir um temporizador de bloqueio automático. Isso bloqueará automaticamente o seu cofre após um período de inatividade.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem a certeza absoluta??</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isto irá eliminar permanentemente a sua conta
e remover todos os seus dados dos nossos servidores, incluindo o seu cofre e palavras-passe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTitle>Aviso</AlertTitle>
              <AlertDescription>
                Todas as suas palavras-passe armazenadas e dados seguros serão eliminados permanentemente.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Digite a sua palavra-passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="A sua palavra-passe atual"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button 
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Eliminando..." : "Eliminar conta"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccountSettings;
